import { useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Download, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { fmtDate, fmtNum, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";
import { HideWhenSingleCompany } from "@/components/CompanyField";

export default function AgingReports() {
  const { companies } = useCompany() || {};
  const [tab, setTab] = useState("aging_payables");
  const [companyId, setCompanyId] = useState("");
  const [asOf, setAsOf] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());

  const effectiveCompanyId = companyId || companies?.[0]?.id;

  const load = async () => {
    if (!effectiveCompanyId) return;
    setLoading(true);
    try {
      const endpoint = tab === "aging_payables" ? "aging-payables" : "aging-receivables";
      const { data } = await api.get(`/reports/${endpoint}`, { params: { company_id: effectiveCompanyId, as_of: asOf || undefined } });
      setData(data);
      setExpanded(new Set());
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format) => {
    try {
      const response = await api.post("/reports/export", { report_key: tab, format, company_id: effectiveCompanyId, as_of: asOf || undefined }, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${tab}.${format === "excel" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const toggle = (id) => setExpanded((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const rows = tab === "aging_payables" ? data?.vendors : data?.clients;
  const nameKey = tab === "aging_payables" ? "vendor_name" : "client_name";
  const idKey = tab === "aging_payables" ? "vendor_id" : "client_id";

  return (
    <div className="space-y-6" data-testid="aging-reports-page">
      <PageHeader
        title="Aging Reports"
        description="Payables by vendor and receivables by client, bucketed 0-30 / 31-60 / 61-90 / 90+ days."
        action={
          <div className="flex items-center gap-2">
            <HideWhenSingleCompany><Select value={companyId || companies?.[0]?.id || ""} onValueChange={setCompanyId}>
              <SelectTrigger className="w-48" data-testid="aging-company-select"><SelectValue placeholder="Company" /></SelectTrigger>
              <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select></HideWhenSingleCompany>
            <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="w-36" data-testid="aging-as-of-input" />
            <Button onClick={load} disabled={loading} data-testid="aging-run-button">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run"}</Button>
          </div>
        }
      />

      <div className="flex flex-wrap gap-1.5">
        <Button size="sm" variant={tab === "aging_payables" ? "default" : "outline"} onClick={() => { setTab("aging_payables"); setData(null); }} data-testid="tab-aging-payables-button">Payables</Button>
        <Button size="sm" variant={tab === "aging_receivables" ? "default" : "outline"} onClick={() => { setTab("aging_receivables"); setData(null); }} data-testid="tab-aging-receivables-button">Receivables</Button>
        {data && (
          <div className="ml-auto flex gap-1.5">
            <Button size="sm" variant="outline" onClick={() => exportReport("excel")} data-testid="aging-export-excel-button"><Download className="h-3.5 w-3.5" /> Excel</Button>
            <Button size="sm" variant="outline" onClick={() => exportReport("pdf")} data-testid="aging-export-pdf-button"><Download className="h-3.5 w-3.5" /> PDF</Button>
          </div>
        )}
      </div>

      {!data || rows.length === 0 ? (
        <EmptyState title={data ? "Nothing outstanding" : "Run a report"} testId="aging-empty-state" />
      ) : (
        <div className="rounded-xl border border-border bg-card">
          <Table>
            <TableHeader><TableRow><TableHead></TableHead><TableHead>{tab === "aging_payables" ? "Vendor" : "Client"}</TableHead><TableHead className="text-right">0-30</TableHead><TableHead className="text-right">31-60</TableHead><TableHead className="text-right">61-90</TableHead><TableHead className="text-right">90+</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <>
                  <TableRow key={r[idKey]} className="cursor-pointer" onClick={() => toggle(r[idKey])} data-testid={`aging-row-${r[idKey]}`}>
                    <TableCell>{expanded.has(r[idKey]) ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}</TableCell>
                    <TableCell className="text-foreground">{r[nameKey]}</TableCell>
                    <TableCell className="text-right font-mono">{fmtNum(r.bucket_0_30, 2)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtNum(r.bucket_31_60, 2)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtNum(r.bucket_61_90, 2)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtNum(r.bucket_over_90, 2)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{fmtNum(r.total, 2)}</TableCell>
                  </TableRow>
                  {expanded.has(r[idKey]) && (
                    <TableRow key={`${r[idKey]}-docs`}>
                      <TableCell></TableCell>
                      <TableCell colSpan={6} className="bg-muted/30 p-0">
                        <div className="space-y-1 p-3" data-testid={`aging-documents-${r[idKey]}`}>
                          {r.documents.map((doc) => (
                            <div key={doc.bill_id} className="flex justify-between text-xs text-muted-foreground">
                              <span>{doc.number} — {fmtDate(doc.bill_date)} ({doc.days_overdue}d)</span>
                              <span className="font-mono text-foreground">{fmtNum(doc.outstanding, 2)}</span>
                            </div>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
